import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NotificationHistoryEntry } from 'src/app/admin/logged-time/notification-management/models/notification-history-entry';
import { formatDateDto } from './helpers/date.helper';
import { EmailPreview } from 'src/app/admin/logged-time/notification-management/models/email-preview';
import { TimeLoggingNotificationSetup } from 'src/app/admin/logged-time/notification-management/models/time-logging-notification-setup';
import { ContentFormat } from 'src/app/shared/models/page/page.model';

@Injectable({
  providedIn: 'root',
})
export class TimeLoggingNotificationService {
  private httpClient = inject(HttpClient);

  private urlBase = 'api/notification-management';

  saveNotificationSetup(
    setup: TimeLoggingNotificationSetup,
    month: moment.Moment,
    force: boolean,
  ): Observable<void> {
    return this.httpClient.post<void>(this.urlBase, {
      plannedLeavesNotificationDate: formatDateDto(setup.plannedLeavesNotificationDate),
      deadlineDate: formatDateDto(setup.deadlineDate),
      setupNotificationDate: formatDateDto(setup.setupNotificationDate),
      workReportNotificationDate: formatDateDto(setup.workReportNotificationDate),
      workReportDeadlineDate: formatDateDto(setup.workReportDeadlineDate),
      timeLogFirstNotificationDate: formatDateDto(setup.missingTimeLogFirstNotificationDate),
      timeLogSecondNotificationDate: formatDateDto(setup.missingTimeLogSecondNotificationDate),
      benefitsNotificationDate: formatDateDto(setup.benefitsNotificationDate),
      benefitsDeadlineDate: formatDateDto(setup.benefitsDeadlineDate),
      payhawkNotificationDate: formatDateDto(setup.payhawkNotificationDate),
      payhawkDeadlineDate: formatDateDto(setup.payhawkDeadlineDate),
      teamsRatesNotificationDate: formatDateDto(setup.teamsRatesNotificationDate),
      teamsRatesDeadlineDate: formatDateDto(setup.teamsRatesDeadlineDate),
      monthlyInvoicesNotificationDate: formatDateDto(setup.monthlyInvoicesNotificationDate),
      monthlyInvoicesDeadlineDate: formatDateDto(setup.monthlyInvoicesDeadlineDate),
      yearMonth: month.format('YYYY-MM'),
      force,
      plannedLeavesCustomBody: setup.plannedLeavesCustomBody ?? null,
      plannedLeavesCustomBodyFormat: setup.plannedLeavesCustomBodyFormat ?? null,
      monthlyInvoicesCustomBody: setup.monthlyInvoicesCustomBody ?? null,
      monthlyInvoicesCustomBodyFormat: setup.monthlyInvoicesCustomBodyFormat ?? null,
    });
  }

  convertBody(content: string, targetFormat: ContentFormat): Observable<string> {
    return this.httpClient
      .post<{ content: string }>(`${this.urlBase}/convert-body`, { content, targetFormat })
      .pipe(map((response) => response.content));
  }

  private toOptionalDate(value: moment.MomentInput | undefined): Date | undefined {
    return value ? moment(value).toDate() : undefined;
  }

  getNotificationSetup(month: string): Observable<TimeLoggingNotificationSetup | null> {
    return this.httpClient
      .get<TimeLoggingNotificationSetup | null>(this.urlBase, { params: { month } })
      .pipe(
        map((setup) => {
          if (!setup) return null;
          return {
            plannedLeavesNotificationDate: moment(setup.plannedLeavesNotificationDate).toDate(),
            deadlineDate: moment(setup.deadlineDate).toDate(),
            setupNotificationDate: this.toOptionalDate(setup.setupNotificationDate),
            workReportNotificationDate: this.toOptionalDate(setup.workReportNotificationDate),
            workReportDeadlineDate: this.toOptionalDate(setup.workReportDeadlineDate),
            missingTimeLogFirstNotificationDate: this.toOptionalDate(
              setup.missingTimeLogFirstNotificationDate,
            ),
            missingTimeLogSecondNotificationDate: this.toOptionalDate(
              setup.missingTimeLogSecondNotificationDate,
            ),
            benefitsNotificationDate: this.toOptionalDate(setup.benefitsNotificationDate),
            benefitsDeadlineDate: this.toOptionalDate(setup.benefitsDeadlineDate),
            payhawkNotificationDate: this.toOptionalDate(setup.payhawkNotificationDate),
            payhawkDeadlineDate: this.toOptionalDate(setup.payhawkDeadlineDate),
            teamsRatesNotificationDate: this.toOptionalDate(setup.teamsRatesNotificationDate),
            teamsRatesDeadlineDate: this.toOptionalDate(setup.teamsRatesDeadlineDate),
            monthlyInvoicesNotificationDate: this.toOptionalDate(
              setup.monthlyInvoicesNotificationDate,
            ),
            monthlyInvoicesDeadlineDate: this.toOptionalDate(setup.monthlyInvoicesDeadlineDate),
            employeeReminderResult: setup.employeeReminderResult,
            opsReminderResult: setup.opsReminderResult,
            workReportLeadsNotificationResult: setup.workReportLeadsNotificationResult,
            timeLogNotificationResult: setup.timeLogNotificationResult,
            benefitsNotificationResult: setup.benefitsNotificationResult,
            payhawkNotificationResult: setup.payhawkNotificationResult,
            teamsRatesNotificationResult: setup.teamsRatesNotificationResult,
            monthlyInvoicesNotificationResult: setup.monthlyInvoicesNotificationResult,
            plannedLeavesCustomBody: setup.plannedLeavesCustomBody,
            plannedLeavesCustomBodyFormat: setup.plannedLeavesCustomBodyFormat,
            monthlyInvoicesCustomBody: setup.monthlyInvoicesCustomBody,
            monthlyInvoicesCustomBodyFormat: setup.monthlyInvoicesCustomBodyFormat,
          };
        }),
      );
  }

  getAvailableMonths(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.urlBase}/available-months`);
  }

  getNotificationHistory(month: string): Observable<NotificationHistoryEntry[]> {
    return this.httpClient.get<NotificationHistoryEntry[]>(`${this.urlBase}/history`, {
      params: { month },
    });
  }

  getPlannedLeavesEmailPreview(
    month: string,
    customBody: string,
    customBodyFormat: ContentFormat,
  ): Observable<EmailPreview> {
    return this.httpClient.post<EmailPreview>(
      `${this.urlBase}/preview/planned-leaves-reminder`,
      { customBody, customBodyFormat },
      { params: { month } },
    );
  }

  getOpsReminderEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/ops-reminder`, {
      params: { month },
    });
  }

  getWorkReportLeadsEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/work-report-leads-reminder`, {
      params: { month },
    });
  }

  getMissingTimeLogEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/missing-time-log`, {
      params: { month },
    });
  }

  getBenefitsReminderEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/benefits-reminder`, {
      params: { month },
    });
  }

  getPayhawkReminderEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/payhawk-reminder`, {
      params: { month },
    });
  }

  getTeamsRatesReminderEmailPreview(month: string): Observable<EmailPreview> {
    return this.httpClient.get<EmailPreview>(`${this.urlBase}/preview/teams-rates-reminder`, {
      params: { month },
    });
  }

  getMonthlyInvoicesReminderEmailPreview(
    month: string,
    customBody: string,
    customBodyFormat: ContentFormat,
  ): Observable<EmailPreview> {
    return this.httpClient.post<EmailPreview>(
      `${this.urlBase}/preview/monthly-invoices-reminder`,
      { customBody, customBodyFormat },
      { params: { month } },
    );
  }
}
